/**
 * Message Converter
 *
 * Converts between different message formats:
 * - Firebase Firestore documents → Frontend messages
 * - Backend (Anthropic) format ↔ Frontend format
 *
 * Two-Role Model:
 * - Only "user" and "assistant" roles are stored in Firestore
 * - Tool interactions are handled via content blocks within messages:
 *   - tool_use: In AssistantMessage content (with HITL fields)
 *   - tool_result: In UserMessage content
 * - StatusMessage is used for UI status updates only
 *
 * @module features/chat/lib/message-converter
 */

import { fromAnthropicFormat } from "@/lib/storage";
import { processContentBlocksForCitations } from "@/constants";
import type { Message } from "../types/sdk";

// =============================================================================
// FIREBASE TYPES
// =============================================================================

/**
 * Firebase message document structure (as stored in Firestore)
 *
 * Two-Role Model:
 * - Only "user" and "assistant" roles for chat messages
 * - "status" role for UI status updates
 * - Tool interactions are content blocks within user/assistant messages:
 *   - tool_use: In AssistantMessage content (with HITL fields)
 *   - tool_result: In UserMessage content
 */
export interface FirebaseMessage {
  id: string;
  role: "user" | "assistant" | "status";
  content?: string | FirebaseContentBlock[];
  created_at?: unknown;
  timestamp?: unknown;
  // Status message fields
  status?: string;
  message?: string;
  metadata?: Record<string, unknown>;
}

/**
 * Firebase content block structure
 *
 * Supports all content block types:
 * - text: Plain text content
 * - image/document: Media content blocks
 * - tool_use: Tool invocation (AssistantMessage content)
 * - tool_result: Tool execution result (UserMessage content)
 * - thinking: Extended thinking content
 */
interface FirebaseContentBlock {
  type: string;
  text?: string;
  data?: string;
  mime_type?: string;
  source_type?: string;
  thumbnail?: string;
  citations?: FirebaseCitation[];
  metadata?: Record<string, unknown>;
  // Tool use fields (in AssistantMessage content)
  id?: string; // tool_use ID
  name?: string; // tool name
  input?: Record<string, unknown>; // tool input parameters
  // HITL fields for tool_use blocks (stored in tool_use content)
  requires_approval?: boolean;
  approval_status?: "pending" | "approved" | "rejected" | "edited";
  original_input?: Record<string, unknown>;
  rejection_message?: string;
  // Tool result fields (in UserMessage content)
  tool_name?: string;
  tool_use_id?: string;
  content?: string | Record<string, unknown> | unknown[];
  frontend_message?: string;
  is_error?: boolean;
  // Thinking fields
  thinking?: string;
  signature?: string;
}

/**
 * Firebase citation structure
 */
interface FirebaseCitation {
  document_id?: string;
  title?: string;
  url?: string;
  text?: string;
}

// =============================================================================
// FIREBASE TO FRONTEND CONVERTERS
// =============================================================================

/**
 * Convert Firebase messages to frontend Message format.
 *
 * Two-Role Model:
 * - Only processes "user", "assistant", and "status" roles
 * - Tool interactions are content blocks within messages, not separate messages
 * - Preserves chronological order (no extraction into separate messages)
 *
 * Validation logging is included to help debug message conversion issues.
 */
export function convertFirebaseMessages(firebaseMessages: FirebaseMessage[]): Message[] {
  const converted: Message[] = [];

  for (const msg of firebaseMessages) {
    // Validate role before conversion (two-role model)
    if (msg.role !== "user" && msg.role !== "assistant" && msg.role !== "status") {
      console.error(`[message-converter] Invalid message role: ${msg.role}`, {
        id: msg.id,
        role: msg.role,
      });
      continue; // Skip invalid messages
    }

    let convertedMsg: Message | Message[];

    switch (msg.role) {
      case "status":
        convertedMsg = convertStatusMessage(msg);
        break;
      case "user":
        convertedMsg = convertUserMessage(msg);
        break;
      case "assistant":
        convertedMsg = convertAssistantMessage(msg);
        break;
      default:
        // This should never happen due to the role validation above
        console.error(`[message-converter] Unexpected role: ${msg.role}`);
        continue;
    }

    // Flatten array results (from convertUserMessage)
    if (Array.isArray(convertedMsg)) {
      converted.push(...convertedMsg);
    } else {
      converted.push(convertedMsg);
    }
  }

  return converted;
}

/**
 * Convert status message
 */
function convertStatusMessage(msg: FirebaseMessage): Message {
  return {
    id: msg.id,
    type: "status",
    status: msg.status || "",
    message: msg.message || "",
    metadata: msg.metadata || {},
  } as Message;
}

/**
 * Convert user message with content blocks.
 *
 * Two-Role Model:
 * - NO extraction of tool_result blocks into separate messages
 * - All content blocks stay within the UserMessage
 * - This preserves chronological order during page reload
 *
 * IMPORTANT: This is the key fix for the duplicate message bug.
 * Previously, tool_result blocks were extracted and created as separate messages,
 * which caused them to appear at the wrong position after page reload.
 */
function convertUserMessage(msg: FirebaseMessage): Message[] {
  let normalizedContent: string | unknown[] | undefined = msg.content;

  if (Array.isArray(msg.content)) {
    // Convert all content blocks WITHOUT extraction
    // Tool result blocks stay in the content array
    normalizedContent = msg.content.map((block) => convertContentBlock(block));
  }

  // Only return a message if it has content
  if (
    (Array.isArray(normalizedContent) && normalizedContent.length > 0) ||
    (!Array.isArray(normalizedContent) && normalizedContent)
  ) {
    return [{
      id: msg.id,
      type: "human",
      content: normalizedContent,
    } as Message];
  }

  // Empty message - return empty array
  return [];
}


/**
 * Convert assistant message with citations.
 *
 * Uses processContentBlocksForCitations() from @/constants/citations
 * as the single source of truth for marker insertion.
 *
 * Preserves thinking, tool_use blocks in the content array so they can be rendered
 * when loading conversation history via onSnapshot.
 *
 * @see @/constants/citations for citation system architecture
 */
function convertAssistantMessage(msg: FirebaseMessage): Message {
  let textContent = "";
  let citations: FirebaseCitation[] = [];
  const contentBlocks: unknown[] = [];

  if (Array.isArray(msg.content)) {
    // Process text blocks for citations
    const processed = processContentBlocksForCitations<FirebaseCitation>(msg.content);
    textContent = processed.text;
    citations = processed.citations;

    // Preserve chronological order from Firestore
    // Iterate through original content array and replace text blocks with merged content
    let textBlockReplaced = false;

    for (const block of msg.content) {
      if (block.type === "thinking") {
        // Keep thinking blocks as-is
        contentBlocks.push(block);
      } else if (block.type === "text" && !textBlockReplaced && textContent) {
        // Replace first text block with merged content (contains all text + citation markers)
        contentBlocks.push({ type: "text", text: textContent });
        textBlockReplaced = true;
      } else if (block.type === "tool_use") {
        // Keep tool_use blocks as-is at their original position
        contentBlocks.push(block);
      }
      // Skip additional text blocks (already merged into first one)
    }

    // Edge case: if no text block existed but we have textContent from string content
    if (!textBlockReplaced && textContent) {
      contentBlocks.push({ type: "text", text: textContent });
    }
  } else if (typeof msg.content === "string") {
    textContent = msg.content;
  }

  return {
    id: msg.id,
    type: "ai",
    content: contentBlocks.length > 0 ? contentBlocks : textContent,
    citations: citations,
  } as Message;
}

/**
 * Convert a single content block from Firebase/Anthropic format to internal format
 */
function convertContentBlock(block: FirebaseContentBlock): unknown {
  // Check if this is Anthropic format or storage format (needs conversion)
  if (block.type === "document" || block.type === "image") {
    // IMPORTANT: Document blocks with audio metadata should be kept as-is
    // They are processed by HumanMessage.tsx to reconstruct audio players
    if (block.type === "document" && block.metadata?.audio) {
      return block;
    }

    const converted = fromAnthropicFormat(block as Parameters<typeof fromAnthropicFormat>[0]);
    if (converted) {
      // Preserve thumbnail from backend (check both root and metadata)
      const thumbnail = block.thumbnail || block.metadata?.thumbnail;
      if (thumbnail) {
        converted.thumbnail = thumbnail as string;
      }
      return converted;
    }
  }

  // Keep text, audio, and already-internal-format blocks as-is
  // Audio blocks are stored in Firestore with full metadata for UI display
  return block;
}

// =============================================================================
// BACKEND ↔ FRONTEND CONVERTERS (Legacy)
// =============================================================================
